<script>
    import { onMount } from 'svelte';

    let groupedClasses = {};
    let filteredGroupedClasses = {};
    let loading = true;
    let activeFilter = 'all'; // 'all', 'today-onwards', 'week'

    // map of classId → boolean (expanded?)
    let expandedMap = {};

    async function handleFetchFirstClasses() {
      const token = localStorage.getItem('jwtToken');
      try {
        const res = await fetch(
          '/studentplayground/api/admin/first-classes-new-teachers/',
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type':  'application/json'
            }
          }
        );
        if (!res.ok) throw new Error(res.statusText);
        groupedClasses = await res.json();
      } catch (err) {
        console.error('Error fetching first classes:', err);
        alert(`Error loading first classes: ${err.message}`);
      } finally {
        loading = false;
      }
    }

    onMount(handleFetchFirstClasses);

    // Reactive statement to filter classes based on selected filter
    $: {
        filteredGroupedClasses = filterClassesByDate(groupedClasses, activeFilter);
    }

    function filterClassesByDate(classes, filter) {
        if (filter === 'all') {
            return classes;
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0); // Set to start of today
        
        let startDate, endDate;
        
        if (filter === 'today-onwards') {
            startDate = new Date(today);
            endDate = null; // No end date for "today onwards"
        } else if (filter === 'week') {
            // 3 days in the past and 3 days in the future
            startDate = new Date(today);
            startDate.setDate(today.getDate() - 3);
            endDate = new Date(today);
            endDate.setDate(today.getDate() + 3);
        }

        const filtered = {};
        
        Object.entries(classes).forEach(([dateString, classArray]) => {
            const classDate = new Date(dateString);
            classDate.setHours(0, 0, 0, 0);
            
            let includeDate = false;
            
            if (filter === 'today-onwards') {
                includeDate = classDate >= startDate;
            } else if (filter === 'week') {
                includeDate = classDate >= startDate && classDate <= endDate;
            }
            
            if (includeDate && classArray.length > 0) {
                filtered[dateString] = classArray;
            }
        });
        
        return filtered;
    }

    function switchFilter(filter) {
        activeFilter = filter;
    }

    // toggle expanded state for one card
    function toggleExpand(id) {
      expandedMap[id] = !expandedMap[id];
      // reassign to trigger Svelte reactivity
      expandedMap = { ...expandedMap };
    }

    function formatDateTime(dateString, timeString) {
      if (!dateString || !timeString) return '-';
      const d = new Date(`${dateString}T${timeString}`);
      // Format in Lithuania timezone
      return `${d.toLocaleDateString('lt-LT', { timeZone: 'Europe/Vilnius' })} at ${timeString}`;
    }

    function formatDateHeader(dateString) {
      if (!dateString) return 'Unknown Date';
      const d = new Date(dateString);
      return d.toLocaleDateString('lt-LT', { 
        weekday: 'long',
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    }

    function getStudentDisplayName(studentName) {
      return studentName || 'Unknown Student';
    }

    function openClassLink(link) {
      if (link) {
        window.open(link, '_blank');
      }
    }

    // Function to handle admin handled/unhandled actions
    async function handleAdminAction(classItem, action) {
      const token = localStorage.getItem('jwtToken');
      
      try {
        const response = await fetch(
          '/studentplayground/api/admin/first-class-encounter-action/',
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              student_id: classItem.student_id,
              teacher_name: classItem.teacher,
              class_date: classItem.class_date,
              gcal_event_id: classItem.gcal_event_id,
              class_name: classItem.class_name,
              action: action
            })
          }
        );

        if (response.ok) {
          const result = await response.json();
          let successMessage;
          if (action === 'mark_handled') {
            successMessage = 'First class encounter marked as handled successfully!';
          } else if (action === 'mark_unhandled') {
            successMessage = 'First class encounter marked as unhandled successfully!';
          } else {
            successMessage = `Encounter ${action} successfully!`;
          }
          alert(successMessage);
          // Refresh the data
          await handleFetchFirstClasses();
        } else {
          const error = await response.json();
          console.error('Failed to perform action:', error);
          alert(`Failed to ${action} encounter: ${error.error || response.statusText}`);
        }
      } catch (error) {
        console.error('Error in admin action:', error);
        alert(`Error ${action}ing encounter: ${error.message}`);
      }
    }
  </script>
  
  <style>
  /* Header styling matching AdminClassRegistrationRequestsComponent */
  .labelDiv {
    display: flex;
    justify-content: space-around;
    margin-bottom: 2rem;
  }
  .labelDiv span {
    font-size: 1.2rem;
    font-weight: bold;
    color: #333;
  }

  /* Loading and empty states */
  .loading-message, .empty-message {
    text-align: center;
    padding: 2rem;
    color: #666;
    font-style: italic;
  }

  /* Date group styling */
  .date-group {
    margin-bottom: 2rem;
  }
  .date-header {
    font-size: 1.1rem;
    font-weight: bold;
    color: #0055aa;
    border-bottom: 2px solid #0055aa;
    padding-bottom: 0.5rem;
    margin-bottom: 1rem;
  }

  /* Filter navigation */
  .filter-navigation {
    display: flex;
    gap: 10px;
    margin-bottom: 2rem;
    justify-content: center;
    flex-wrap: wrap;
  }

  .filter-button {
    padding: 10px 20px;
    border: 1px solid #ccc;
    background: white;
    cursor: pointer;
    border-radius: 8px;
    font-size: 14px;
    transition: all 0.2s ease;
    font-weight: 500;
  }

  .filter-button:hover {
    background-color: #f8f9fa;
    border-color: #0055aa;
    transform: translateY(-1px);
  }

  .filter-button.active {
    background: #0055aa;
    color: white;
    border-color: #0055aa;
    box-shadow: 0 2px 4px rgba(0, 85, 170, 0.3);
  }

  .filter-button.active:hover {
    background: #004488;
  }

  /* Section header */
  .section-header {
    margin: 2rem 0 1rem 0;
    padding: 1.5rem;
    border-radius: 8px;
    text-align: center;
    background: linear-gradient(135deg, #e8f5e8 0%, #d4edda 100%);
    border: 1px solid #c3e6cb;
    color: #155724;
  }
  .section-header h2 {
    margin: 0 0 0.5rem 0;
    font-size: 1.3rem;
    font-weight: bold;
  }
  .section-header p {
    margin: 0;
    font-size: 0.95rem;
    opacity: 0.8;
  }
  .results-count {
    margin-top: 0.5rem;
    font-size: 0.85rem;
    font-weight: 600;
    opacity: 0.9;
  }

  /* Class card styling */
  .class-card {
    border: 1px solid #ccc;
    border-radius: 8px;
    margin-bottom: 1rem;
    background: #fff;
    overflow: hidden;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    transition: box-shadow 0.2s ease;
  }
  .class-card:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  /* Summary button styling */
  .class-summary {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 1rem;
    border: none;
    background: #fff;
    cursor: pointer;
    text-align: left;
    transition: background-color 0.2s ease;
    font-size: 1rem;
    border-left: 4px solid #28a745;
  }
  .class-summary:hover {
    background-color: #f8f9fa;
  }

  /* Summary content layout */
  .summary-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex: 1;
    margin-right: 1rem;
  }
  .summary-main {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
  }
  .student-name {
    font-weight: bold;
    color: #333;
    font-size: 1rem;
  }
  .teacher-info {
    color: #666;
    font-size: 0.9rem;
  }
  .first-class-badge {
    background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
    color: #856404;
    padding: 0.3rem 0.8rem;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: bold;
    text-transform: uppercase;
    border: 1px solid #ffeaa7;
  }

  /* Handled status badge */
  .handled-badge {
    padding: 0.3rem 0.8rem;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: bold;
    text-transform: uppercase;
  }
  .handled-badge.handled {
    background-color: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
  }
  .handled-badge.unhandled {
    background-color: #fff3cd;
    color: #856404;
    border: 1px solid #ffeaa7;
  }

  /* Time badge */
  .time-badge {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
    align-items: flex-end;
  }
  .class-time {
    background-color: #e1f5fe;
    color: #01579b;
    padding: 0.3rem 0.8rem;
    border-radius: 12px;
    font-size: 0.85rem;
    font-weight: bold;
    border: 1px solid #b3e5fc;
  }

  /* Expand icon */
  .expand-icon {
    color: #0055aa;
    font-size: 0.8rem;
    transition: transform 0.2s ease;
  }

  /* Details section */
  .class-details {
    padding: 1.5rem;
    background-color: #f8f9fa;
    border-top: 1px solid #e9ecef;
  }
  .details-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
  }
  .detail-item {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
  }
  .detail-item.full-width {
    grid-column: 1 / -1;
  }
  .detail-label {
    font-weight: bold;
    color: #0055aa;
    font-size: 0.9rem;
  }
  .detail-value {
    color: #333;
    font-size: 1rem;
    padding: 0.4rem;
    background-color: #fff;
    border: 1px solid #ddd;
    border-radius: 4px;
  }

  /* Special highlight for first class */
  .first-class-highlight {
    background: linear-gradient(135deg, #fff3cd 0%, #fff8dc 100%);
    border: 1px solid #ffeaa7;
    color: #856404;
    padding: 0.8rem;
    border-radius: 6px;
    font-size: 0.9rem;
    line-height: 1.4;
    font-weight: 600;
  }

  /* Handled info styling */
  .handled-info {
    background: #d4edda;
    color: #155724;
    padding: 0.8rem;
    border-radius: 6px;
    border: 1px solid #c3e6cb;
    font-size: 0.9rem;
    line-height: 1.4;
  }

  .handled-info strong {
    font-weight: 600;
  }

  /* Parent information styling */
  .parent-info {
    background: #e8f4fd;
    color: #0c5460;
    padding: 0.8rem;
    border-radius: 6px;
    border: 1px solid #bee5eb;
    font-size: 0.9rem;
    line-height: 1.6;
  }

  .parent-info div {
    margin-bottom: 0.3rem;
  }

  .parent-info div:last-child {
    margin-bottom: 0;
  }

  .parent-info strong {
    font-weight: 600;
  }

  .parent-info-none {
    background: #f8f9fa;
    color: #6c757d;
    padding: 0.8rem;
    border-radius: 6px;
    border: 1px solid #dee2e6;
    font-size: 0.9rem;
    font-style: italic;
  }

  /* Action buttons */
  .action-buttons {
    display: flex;
    gap: 1rem;
    margin-top: 0.5rem;
    flex-wrap: wrap;
  }
  
  .action-button {
    padding: 0.8rem 1.5rem;
    border: none;
    border-radius: 6px;
    font-weight: bold;
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: 0.9rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  .handled-button {
    background-color: #28a745;
    color: white;
  }
  
  .handled-button:hover {
    background-color: #218838;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(40, 167, 69, 0.3);
  }
  
  .unhandled-button {
    background-color: #dc3545;
    color: white;
  }
  
  .unhandled-button:hover {
    background-color: #c82333;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(220, 53, 69, 0.3);
  }
  
  .join-button {
    background-color: #17a2b8;
    color: white;
  }
  
  .join-button:hover {
    background-color: #138496;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(23, 162, 184, 0.3);
  }
  
  .action-button:active {
    transform: translateY(0);
  }

  /* Responsive adjustments */
  @media (max-width: 768px) {
    .details-grid {
      grid-template-columns: 1fr;
    }
    .summary-content {
      flex-direction: column;
      align-items: flex-start;
      gap: 0.5rem;
    }
    .time-badge {
      align-self: flex-end;
    }
    .action-buttons {
      flex-direction: column;
      gap: 0.5rem;
    }
    .action-button {
      justify-content: center;
    }
  }
</style>
  
  <div class="labelDiv">
  <span>FIRST CLASSES WITH NEW TEACHERS</span>
</div>

<!-- Filter Navigation -->
<div class="filter-navigation">
  <button 
    class="filter-button {activeFilter === 'all' ? 'active' : ''}"
    on:click={() => switchFilter('all')}
  >
    All
  </button>
  <button 
    class="filter-button {activeFilter === 'today-onwards' ? 'active' : ''}"
    on:click={() => switchFilter('today-onwards')}
  >
    Today Onwards
  </button>
  <button 
    class="filter-button {activeFilter === 'week' ? 'active' : ''}"
    on:click={() => switchFilter('week')}
  >
    Week (±3 days)
  </button>
</div>

{#if loading}
  <div class="loading-message">Loading first classes data…</div>
{:else if Object.keys(groupedClasses).length === 0}
  <div class="empty-message">No first classes with new teachers found.</div>
{:else if Object.keys(filteredGroupedClasses).length === 0}
  <div class="empty-message">No first classes found for the selected filter.</div>
{:else}
  <div class="section-header">
    <h2>🎯 Student-Teacher First Encounters</h2>
    <p>Students meeting their new teachers for the first time</p>
    <div class="results-count">
      Showing {Object.values(filteredGroupedClasses).flat().length} encounters
      {#if activeFilter !== 'all'}
        ({activeFilter === 'today-onwards' ? 'from today onwards' : 'within ±3 days'})
      {/if}
    </div>
  </div>
  
  {#each Object.entries(filteredGroupedClasses).sort((a, b) => a[0].localeCompare(b[0])) as [day, classes]}
    <div class="date-group">
      <h3 class="date-header">{formatDateHeader(day)}</h3>
      {#each classes as classItem (classItem.id)}
        <div class="class-card">
          <!-- Summary Header -->
          <button
            type="button"
            class="class-summary"
            on:click={() => toggleExpand(classItem.id)}
          >
            <div class="summary-content">
              <div class="summary-main">
                <span class="student-name">{getStudentDisplayName(classItem.student_name)}</span>
                <span class="teacher-info">First class with: <strong>{classItem.teacher}</strong></span>
              </div>
              <div class="time-badge">
                <div class="first-class-badge">First Encounter</div>
                <div class="handled-badge {classItem.is_handled ? 'handled' : 'unhandled'}">
                  {classItem.is_handled ? 'Handled' : 'Unhandled'}
                </div>
                <div class="class-time">{classItem.class_time}</div>
              </div>
            </div>
            <div class="expand-icon">
              {expandedMap[classItem.id] ? '▼' : '▶'}
            </div>
          </button>

          <!-- Expanded Details -->
          {#if expandedMap[classItem.id]}
            <div class="class-details">
              <div class="details-grid">
                <div class="detail-item">
                  <span class="detail-label">Student Name:</span>
                  <span class="detail-value">{classItem.student_name}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Student Email:</span>
                  <span class="detail-value">{classItem.student_email || 'N/A'}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Student Phone:</span>
                  <span class="detail-value">{classItem.student_phone || 'N/A'}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Teacher:</span>
                  <span class="detail-value">{classItem.teacher}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Class Time:</span>
                  <span class="detail-value">{formatDateTime(classItem.class_date, classItem.class_time)}</span>
                </div>
                <div class="detail-item full-width">
                  <span class="detail-label">Class Name:</span>
                  <span class="detail-value">{classItem.class_name}</span>
                </div>
                <div class="detail-item full-width">
                  <span class="detail-label">Display Time:</span>
                  <span class="detail-value">{classItem.display_time || 'N/A'}</span>
                </div>
                
                <!-- Parent Information Section -->
                {#if classItem.parent_name || classItem.parent_phone || classItem.parent_email}
                  <div class="detail-item full-width">
                    <span class="detail-label">Parent Information:</span>
                    <div class="parent-info">
                      {#if classItem.parent_name}
                        <div><strong>👤 Parent Name:</strong> {classItem.parent_name}</div>
                      {/if}
                      {#if classItem.parent_phone}
                        <div><strong>📞 Parent Phone:</strong> {classItem.parent_phone}</div>
                      {/if}
                      {#if classItem.parent_email}
                        <div><strong>📧 Parent Email:</strong> {classItem.parent_email}</div>
                      {/if}
                    </div>
                  </div>
                {:else}
                  <div class="detail-item full-width">
                    <span class="detail-label">Parent Information:</span>
                    <div class="parent-info-none">
                      ℹ️ No parent information available
                    </div>
                  </div>
                {/if}
                
                <!-- Special highlight for first class -->
                <div class="detail-item full-width">
                  <span class="detail-label">Significance:</span>
                  <div class="first-class-highlight">
                    🎯 <strong>First Time Meeting:</strong> This is the first class where {classItem.student_name} will work with {classItem.teacher}. This represents the beginning of their student-teacher relationship.
                  </div>
                </div>
                
                <!-- Handled status info -->
                {#if classItem.is_handled && classItem.handled_by}
                  <div class="detail-item full-width">
                    <span class="detail-label">Handled Status:</span>
                    <div class="handled-info">
                      <strong>✓ Handled by:</strong> {classItem.handled_by}<br>
                      <strong>Handled at:</strong> {formatDateTime(classItem.handled_at?.split('T')[0], classItem.handled_at?.split('T')[1]?.split('.')[0])}
                      {#if classItem.notes}
                        <br><strong>Notes:</strong> {classItem.notes}
                      {/if}
                    </div>
                  </div>
                {/if}
                
                <!-- Admin action buttons -->
                <div class="detail-item full-width">
                  <span class="detail-label">Admin Actions:</span>
                  <div class="action-buttons">
                    {#if classItem.is_handled}
                      <button 
                        class="action-button unhandled-button"
                        on:click={() => handleAdminAction(classItem, 'mark_unhandled')}
                      >
                        ✗ Mark as Unhandled
                      </button>
                    {:else}
                      <button 
                        class="action-button handled-button"
                        on:click={() => handleAdminAction(classItem, 'mark_handled')}
                      >
                        ✓ Mark as Handled
                      </button>
                    {/if}
                    
                    {#if classItem.class_link}
                      <button 
                        class="action-button join-button"
                        on:click={() => openClassLink(classItem.class_link)}
                      >
                        🔗 Join Class
                      </button>
                    {/if}
                  </div>
                </div>
                
                <!-- Additional metadata -->
                <div class="detail-item">
                  <span class="detail-label">Event ID:</span>
                  <span class="detail-value">{classItem.gcal_event_id || 'N/A'}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Student ID:</span>
                  <span class="detail-value">#{classItem.student_id}</span>
                </div>
              </div>
            </div>
          {/if}
        </div>
      {/each}
    </div>
  {/each}
{/if}
